import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { MatrixRain } from "@/components/MatrixRain";


const DEFAULT_SHIPPING = {
    CA: {
        standard: 7.99,
        express: 12.99,
    },
    US: {
        standard: 5.99,
        express: 9.99,
    },
};

const EMPTY_FORM = {
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "",
    postalCode: "",
    country: "Canada",
};

function money(value, region = "CA") {
    const currency = region === "US" ? "USD" : "CAD";
    const locale = region === "US" ? "en-US" : "en-CA";

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
    }).format(value);
}

function Field({
    label,
    name,
    value,
    onChange,
    type = "text",
    placeholder = "",
    required = true,
}) {
    return (
        <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300/80">
                {label}
            </span>

            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                className="
                    w-full
                    rounded-lg
                    border
                    border-emerald-400/20
                    bg-black/50
                    px-2
                    py-1
                    text-sm
                    text-emerald-100
                    outline-none
                    transition
                    placeholder:text-emerald-100/25
                    focus:border-emerald-400/60
                    focus:ring-1
                    focus:ring-emerald-400/40
                "
            />
        </label>
    );
}

function Panel({ title, children }) {
    return (
        <section
            className="
                rounded-2xl
                border
                border-emerald-400/20
                bg-black/55
                p-5
                shadow-[0_0_40px_rgba(16,185,129,0.08)]
                backdrop-blur-md
            "
        >
            <div className="mb-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-emerald-400/20" />

                <h2 className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-300">
                    {title}
                </h2>

                <div className="h-px flex-1 bg-emerald-400/20" />
            </div>

            {children}
        </section>
    );
}

function OrderSummary({
    cart,
    subtotal,
    shipping,
    total,
    region,
    onQuantityChange,
    onRemove,
}) {
    return (
        <aside
            className="
                rounded-2xl
                border
                border-emerald-400/20
                bg-black/60
                p-5
                shadow-[0_0_50px_rgba(16,185,129,0.08)]
                backdrop-blur-md
                lg:sticky
                lg:top-6
            "
        >
            <div className="mb-6">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/70">
                    Comik Releaf
                </p>

                <h2 className="mt-1 text-2xl font-black uppercase tracking-wider text-emerald-100">
                    Order Summary
                </h2>
            </div>

            <div className="space-y-4">
                {cart.map((item) => (
                    <div
                        key={item.id}
                        className="
                            rounded-xl
                            border
                            border-emerald-400/10
                            bg-black/40
                            p-3
                        "
                    >
                        <div className="flex gap-3">
                            {item.image ? (
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="
                                        h-20
                                        w-20
                                        rounded-lg
                                        border
                                        border-emerald-400/20
                                        object-cover
                                    "
                                />
                            ) : (
                                <div
                                    className="
                                        flex
                                        h-20
                                        w-20
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-emerald-400/20
                                        bg-emerald-400/5
                                        text-xs
                                        uppercase
                                        tracking-wider
                                        text-emerald-400/40
                                    "
                                >
                                    No Image
                                </div>
                            )}

                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-sm font-bold text-emerald-100">
                                    {item.title}
                                </h3>

                                {item.variant && (
                                    <p className="mt-1 text-xs text-emerald-100/50">
                                        {item.variant}
                                    </p>
                                )}

                                <p className="mt-2 text-sm font-semibold text-emerald-300">
                                    {money(item.price, region)}
                                </p>
                            </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        onQuantityChange?.(
                                            item.id,
                                            Math.max(1, item.quantity - 1)
                                        )
                                    }
                                    className="
                                        flex
                                        h-7
                                        w-7
                                        items-center
                                        justify-center
                                        rounded
                                        border
                                        border-emerald-400/20
                                        text-emerald-300
                                        transition
                                        hover:border-emerald-400/50
                                        hover:bg-emerald-400/10
                                    "
                                >
                                    −
                                </button>

                                <span className="min-w-8 text-center text-sm text-emerald-100">
                                    {item.quantity}
                                </span>

                                <button
                                    type="button"
                                    onClick={() =>
                                        onQuantityChange?.(
                                            item.id,
                                            item.quantity + 1
                                        )
                                    }
                                    className="
                                        flex
                                        h-7
                                        w-7
                                        items-center
                                        justify-center
                                        rounded
                                        border
                                        border-emerald-400/20
                                        text-emerald-300
                                        transition
                                        hover:border-emerald-400/50
                                        hover:bg-emerald-400/10
                                    "
                                >
                                    +
                                </button>
                            </div>

                            <button
                                type="button"
                                onClick={() => onRemove?.(item.id)}
                                className="
                                    text-xs
                                    uppercase
                                    tracking-wider
                                    text-red-400/70
                                    transition
                                    hover:text-red-300
                                "
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="my-6 h-px bg-emerald-400/15" />

            <div className="space-y-3 text-sm">
                <div className="flex justify-between text-emerald-100/60">
                    <span>Subtotal</span>

                    <span>{money(subtotal, region)}</span>
                </div>

                <div className="flex justify-between text-emerald-100/60">
                    <span>Shipping</span>

                    <span>{money(shipping, region)}</span>
                </div>

                <div className="flex items-center justify-between text-emerald-100/60">
                    <div>
                        <span>Taxes Included</span>

                        <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-emerald-400/50">
                            LIMITED TIME ONLY
                        </div>
                    </div>
                </div>
            </div>

            <div className="my-5 h-px bg-emerald-400/20" />

            <div className="flex items-end justify-between">
                <span className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-300">
                    Total
                </span>

                <span className="text-2xl font-black text-emerald-100">
                    {money(total, region)}
                </span>
            </div>
        </aside>
    );
}

export default function CheckoutPage({
    cart = [],
    shippingRates = DEFAULT_SHIPPING,
    backgroundImage = "",
    onBackToStore,
    onQuantityChange,
    onRemove,
    onPlaceOrder,
}) {
    const navigate = useNavigate();
    
    const [form, setForm] = useState(EMPTY_FORM);

    const [shippingMethod, setShippingMethod] = useState("standard");
    const [region] = useState(() => {
        return localStorage.getItem("comikReleafRegion") || "CA";
    });    

    const [paymentMethod, setPaymentMethod] = useState("stripe");

    const [error, setError] = useState("");

    const subtotal = useMemo(() => {
        return cart.reduce((sum, item) => {
            return sum + Number(item.price || 0) * Number(item.quantity || 0);
        }, 0);
    }, [cart]);

    const regionalShippingRates =
        shippingRates[region] ?? DEFAULT_SHIPPING[region];

    const shipping =
        regionalShippingRates[shippingMethod] ?? 0;

    const total = subtotal + shipping;

    function handleChange(event) {
        const { name, value } = event.target;

        setForm((current) => ({
            ...current,
            [name]: value,
        }));
    }

    function handleSubmit(event) {
        event.preventDefault();
        setError("");

        if (!cart.length) {
            setError(
                "Your cart is empty. Add an item before continuing to checkout."
            );
            return;
        }

        const countryCode = form.country.trim().toUpperCase();
        const isCanadianCheckout =
            countryCode === "CA" || countryCode === "CANADA";

        if (isCanadianCheckout && !form.phone.trim()) {
            setError(
                "Please enter your phone number for Canadian shipping."
            );
            return;
        }

        const backendUrl = (
            process.env.REACT_APP_BACKEND_URL || ""
        ).replace(/\/+$/, "");

        if (!backendUrl) {
            setError(
                "Checkout is unavailable because the backend URL is not configured."
            );
            return;
        }

        const checkoutPayload = {
            customer: {
                name: form.fullName,
                email: form.email,
                phone: form.phone.trim(),
                shipping_address: {
                    name: form.fullName,
                    address_line1: form.address,
                    address_line2: "",
                    city: form.city,
                    state: form.province,
                    postal_code: form.postalCode,
                    country: form.country,
                },
                marketing_consent: false,
                marketing_consent_at: null,
            },

            items: cart.map((item) => ({
                product_id: item.productId,
                variant_id: item.variantId,
                quantity: Number(item.quantity || 1),
            })),

            shipping_method: shippingMethod,
        };

        fetch(`${backendUrl}/api/orders`, {
            method: "POST",

            headers: {
                "Content-Type": "application/json",
            },

            body: JSON.stringify(checkoutPayload),
        })
            .then(async (response) => {
                const data = await response.json();

                if (!response.ok || data.error) {
                    throw new Error(
                        data.error ||
                            "Unable to create the checkout session."
                    );
                }

                if (!data.checkout_url) {
                    throw new Error(
                        "Stripe checkout URL was not returned. The backend response did not contain a checkout URL."
                    );
                }

                return data;
            })
            .then((data) => {
                window.location.href = data.checkout_url;
            })
            .catch((error) => {
                console.error(
                    "Checkout creation failed:",
                    error
                );

                setError(
                    error.message ||
                        "Unable to continue to payment."
                );
            });
    }

    return (
        <div
            className="
                relative
                min-h-screen
                overflow-hidden
                bg-black
                text-emerald-100
            "
        >

        {/* Phoenix background video */}
        <video
            src="/assets/Matrix_phoenix_bird.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
            aria-hidden="true"
        />

        
        {/* Falling Matrix 0s and 1s */}
        <MatrixRain />

            {backgroundImage && (
                <div
                    className="
                        pointer-events-none
                        absolute
                        inset-0
                        bg-cover
                        bg-center
                        bg-no-repeat
                    "
                    style={{
                        backgroundImage: `
                            linear-gradient(
                                rgba(0, 10, 6, 0.78),
                                rgba(0, 10, 6, 0.92)
                            ),
                            url("${backgroundImage}")
                        `,
                    }}
                />
            )}

            <div
                className="
                    pointer-events-none
                    absolute
                    inset-0
                    opacity-20
                "
                style={{
                    backgroundImage: `
                        linear-gradient(
                            rgba(16, 185, 129, 0.08) 1px,
                            transparent 1px
                        ),
                        linear-gradient(
                            90deg,
                            rgba(16, 185, 129, 0.08) 1px,
                            transparent 1px
                        )
                    `,
                    backgroundSize: "40px 40px",
                }}
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-[radial-gradient(
                        circle_at_center,
                        rgba(16,185,129,0.12),
                        transparent_60%
                    )]
                "
            />

            <div className="relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <header className="mb-8">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <button
                                type="button"
                                onClick={() => {
                                    if (typeof onBackToStore === "function") {
                                        onBackToStore();
                                        return;
                                    }

                                    navigate("/comik-releaf");
                                }}
                                className="
                                    mb-4
                                    text-xs
                                    font-semibold
                                    uppercase
                                    tracking-[0.25em]
                                    text-emerald-400/70
                                    transition
                                    hover:text-emerald-300
                                "
                            >
                                ← Back to Store
                            </button>

                            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-emerald-400">
                                Doriantelive // Comik Releaf
                            </p>

                            <h1
                                className="
                                    mt-2
                                    text-4xl
                                    font-black
                                    uppercase
                                    tracking-tight
                                    text-emerald-100
                                    sm:text-5xl
                                "
                            >
                                Checkout
                            </h1>
                        </div>

                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider">
                            <span className="font-bold text-emerald-300">
                                01 Shipping
                            </span>

                            <span className="text-emerald-400/30">
                                //
                            </span>

                            <span className="text-emerald-100/40">
                                02 Payment
                            </span>

                            <span className="text-emerald-400/30">
                                //
                            </span>

                            <span className="text-emerald-100/40">
                                03 Confirmation
                            </span>
                        </div>
                    </div>
                </header>

                {!cart.length ? (
                    <div
                        className="
                            rounded-2xl
                            border
                            border-emerald-400/20
                            bg-black/60
                            p-10
                            text-center
                            backdrop-blur-md
                        "
                    >
                        <h2 className="text-2xl font-black uppercase text-emerald-100">
                            Cart Empty
                        </h2>

                        <p className="mx-auto mt-3 max-w-lg text-sm text-emerald-100/50">
                            There are currently no products in your cart.
                            Return to the storefront to select something from
                            the collection.
                        </p>

                        <button
                            type="button"
                            onClick={() => {
                                if (typeof onBackToStore === "function") {
                                    onBackToStore();
                                    return;
                                }

                                navigate("/comik-releaf");
                            }}
                            className="
                                mt-6
                                rounded-lg
                                border
                                border-emerald-400/40
                                bg-emerald-400/10
                                px-6
                                py-3
                                text-xs
                                font-bold
                                uppercase
                                tracking-[0.2em]
                                text-emerald-300
                                transition
                                hover:border-emerald-400/70
                                hover:bg-emerald-400/20
                            "
                        >
                            Return to Store
                        </button>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="
                            grid
                            gap-6
                            lg:grid-cols-[minmax(0,1fr)_380px]
                        "
                    >
                        <div className="space-y-4">
                            <Panel title="Shipping Information">
                                <div className="grid gap-2 sm:grid-cols-2">
                                    <div className="sm:col-span-2">
                                        <Field
                                            label="Full Name"
                                            name="fullName"
                                            value={form.fullName}
                                            onChange={handleChange}
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div className="sm:col-span-2">
                                        <Field
                                            label={
                                                form.country.trim().toUpperCase() === "CA" ||
                                                form.country.trim().toUpperCase() === "CANADA"
                                                    ? "Phone Number (Required for Canadian Shipping)"
                                                    : "Phone Number (Optional)"
                                            }
                                            name="phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={handleChange}
                                            placeholder="e.g. 506-555-0123"
                                            required={
                                                form.country.trim().toUpperCase() === "CA" ||
                                                form.country.trim().toUpperCase() === "CANADA"
                                            }
                                        />
                                    </div>

                                    <div className="sm:col-span-2">
                                        <Field
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            placeholder="you@example.com"
                                        />
                                    </div>

                                    <div className="sm:col-span-2">
                                        <Field
                                            label="Address"
                                            name="address"
                                            value={form.address}
                                            onChange={handleChange}
                                            placeholder="123 Main Street"
                                        />
                                    </div>

                                    <Field
                                        label="City"
                                        name="city"
                                        value={form.city}
                                        onChange={handleChange}
                                        placeholder="Moncton"
                                    />

                                    <Field
                                        label="Province"
                                        name="province"
                                        value={form.province}
                                        onChange={handleChange}
                                        placeholder="New Brunswick"
                                    />

                                    <Field
                                        label="Postal Code"
                                        name="postalCode"
                                        value={form.postalCode}
                                        onChange={handleChange}
                                        placeholder="E1A 1A1"
                                    />

                                    <Field
                                        label="Country"
                                        name="country"
                                        value={form.country}
                                        onChange={handleChange}
                                        placeholder="Canada"
                                    />
                                </div>
                            </Panel>

                            <Panel title="Shipping Method">
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <label
                                        className={`
                                            flex
                                            cursor-pointer
                                            items-center
                                            justify-between
                                            rounded-xl
                                            border
                                            p-4
                                            transition
                                            ${
                                                shippingMethod === "standard"
                                                    ? "border-emerald-400/50 bg-emerald-400/10"
                                                    : "border-emerald-400/10 bg-black/30 hover:border-emerald-400/30"
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                name="shippingMethod"
                                                value="standard"
                                                checked={
                                                    shippingMethod ===
                                                    "standard"
                                                }
                                                onChange={(event) =>
                                                    setShippingMethod(
                                                        event.target.value
                                                    )
                                                }
                                                className="accent-emerald-400"
                                            />

                                            <div>
                                                <p className="text-sm font-bold text-emerald-100">
                                                    Standard Shipping
                                                </p>

                                                <p className="mt-1 text-xs text-emerald-100/40">
                                                    Regular delivery
                                                </p>
                                            </div>
                                        </div>

                                        <span className="text-sm font-semibold text-emerald-300">
                                            {money(regionalShippingRates.standard, region)}
                                        </span>
                                    </label>

                                    <label
                                        className={`
                                            flex
                                            cursor-pointer
                                            items-center
                                            justify-between
                                            rounded-xl
                                            border
                                            p-4
                                            transition
                                            ${
                                                shippingMethod === "express"
                                                    ? "border-emerald-400/50 bg-emerald-400/10"
                                                    : "border-emerald-400/10 bg-black/30 hover:border-emerald-400/30"
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                name="shippingMethod"
                                                value="express"
                                                checked={
                                                    shippingMethod ===
                                                    "express"
                                                }
                                                onChange={(event) =>
                                                    setShippingMethod(
                                                        event.target.value
                                                    )
                                                }
                                                className="accent-emerald-400"
                                            />

                                            <div>
                                                <p className="text-sm font-bold text-emerald-100">
                                                    Express Shipping
                                                </p>

                                                <p className="mt-1 text-xs text-emerald-100/40">
                                                    Faster delivery
                                                </p>
                                            </div>
                                        </div>

                                        <span className="text-sm font-semibold text-emerald-300">
                                            {money(regionalShippingRates.express, region)}
                                        </span>
                                    </label>
                                </div>
                            </Panel>

                            <Panel title="Payment">
                                <div>
                                    <div
                                        className="
                                            flex
                                            items-center
                                            gap-3
                                            rounded-xl
                                            border
                                            border-emerald-400/50
                                            bg-emerald-400/10
                                            p-4
                                        "
                                    >
                                        <span
                                            className="
                                                flex
                                                h-4
                                                w-4
                                                shrink-0
                                                items-center
                                                justify-center
                                                rounded-full
                                                border
                                                border-emerald-400
                                            "
                                        >
                                            <span className="h-2 w-2 rounded-full bg-emerald-400" />
                                        </span>

                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-emerald-100">
                                                Secure Payment
                                            </p>

                                            <p className="mt-1 text-xs text-emerald-100/40">
                                                Powered by Stripe
                                            </p>
                                        </div>

                                        <div className="flex shrink-0 items-center gap-1.5">
                                            <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-black text-blue-700">
                                                VISA
                                            </span>

                                            <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-black text-red-600">
                                                MC
                                            </span>

                                            <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-black text-blue-600">
                                                AMEX
                                            </span>

                                            <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-black text-blue-800">
                                                PayPal
                                            </span>
                                        </div>
                                    </div>

                                    <p className="mt-3 text-xs leading-5 text-emerald-100/40">
                                        Secure payment through Stripe. Available payment methods are
                                        presented when you continue to payment.
                                    </p>
                                </div>
                            </Panel>

                            {error && (
                                <div
                                    className="
                                        rounded-xl
                                        border
                                        border-red-400/30
                                        bg-red-400/5
                                        p-4
                                        text-sm
                                        text-red-300
                                    "
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="
                                    w-full
                                    rounded-xl
                                    border
                                    border-emerald-400/50
                                    bg-emerald-400/10
                                    px-6
                                    py-4
                                    text-sm
                                    font-black
                                    uppercase
                                    tracking-[0.25em]
                                    text-emerald-200
                                    shadow-[0_0_30px_rgba(16,185,129,0.08)]
                                    transition
                                    hover:border-emerald-300
                                    hover:bg-emerald-400/20
                                    hover:text-emerald-100
                                "
                            >
                                Complete Order
                            </button>
                        </div>

                        <OrderSummary
                            cart={cart}
                            subtotal={subtotal}
                            shipping={shipping}
                            total={total}
                            region={region}
                            onQuantityChange={onQuantityChange}
                            onRemove={onRemove}
                        />
                    </form>
                )}
            </div>
        </div>
    );
}